/**
 * What a client may SEND when authoring catalog content.
 *
 * Separate from the read schemas because the two genuinely differ: derived
 * fields are absent here by construction, and server-owned counters are not
 * a client's to set.
 *
 * snake_case, like every other wire shape — the API is the one place tomo
 * speaks it. Stored documents and the domain use camelCase, and @tomo/data
 * maps. Using one casing for both is what made a bundle invisible to its own
 * channel-scoped query.
 *
 * L16 — the update path is as strong as the create path. Theirs was weaker,
 * and could edit a valid bundle into a broken one: `chance` bounds and
 * `itemsPerBundle` were never re-checked on update, so a bundle that passed
 * every guard on the way in could be saved back broken.
 */
import { z } from 'zod';
import { MAX_PACK_SIZE, MAX_SLOTS } from './catalog.js';
import { TWITCH_TRIGGER_TYPES, TriggerConditions } from './triggers.js';

/**
 * Supply, as authored.
 *
 * L8 — `null` (uncapped) and `0` (deliberately not printed) are OPPOSITES, so
 * this is a NULLABLE field rather than an optional one. Omitting it would make
 * "I forgot" indistinguishable from "never mint this", and the difference is
 * an infinite print run.
 */
const SupplyInput = z.object({
  limit: z.number().int().min(0).nullable(),
});

/**
 * One reward a slot may draw, and how heavily.
 *
 * A relative weight, not a percentage: weights inside a slot are normalised,
 * so 3/1 and 75/25 say the same thing. Their slot editor made every row total
 * exactly 100 and it is a rule people fight rather than a rule that helps.
 */
const SlotEntryInput = z.object({
  reward_id: z.string().min(1),
  weight: z.number().min(0).max(1_000_000).default(1),
});

const SlotInput = z.object({
  label: z.string().min(1).max(60).optional(),
  count: z.number().int().min(1).max(20).default(1),
  weighting: z.enum(['explicit', 'by_remaining']).default('explicit'),
  entries: z.array(SlotEntryInput).min(1).max(50),
})
  .refine(
    (s) => new Set(s.entries.map((e) => e.reward_id)).size === s.entries.length,
    {
      // Two rows for one reward are two weights for one thing, and which one
      // applies depends on iteration order. Naming it twice is always a
      // mistake, and the fix — one row, the weights added — is unambiguous.
      message: 'A slot names each reward at most once. Combine the duplicate rows into one weight.',
      path: ['entries'],
    }
  )
  .refine(
    (s) => s.weighting !== 'explicit' || s.entries.some((e) => e.weight > 0),
    {
      // Every weight zero means the slot can never draw anything, and L4 says
      // a zero-weight entry is not a candidate — so this slot would fail at
      // open time, in front of an audience, having saved cleanly.
      message: 'Every weight in this slot is zero, so it could never draw anything.',
      path: ['entries'],
    }
  );

export const BundleFields = z.object({
  name: z.string().min(1).max(120),
  /**
   * The loot table.
   *
   * L16 applies as it did to `items_per_bundle`: these bounds are re-checked
   * on UPDATE, not only on create. Theirs were create-only, so a bundle that
   * passed every guard on the way in could be saved back broken.
   */
  slots: z.array(SlotInput).min(1).max(MAX_SLOTS),
  supply: SupplyInput,
  active: z.boolean().default(true),
});

export const BundleInput = BundleFields
  .refine(
    (b) => b.slots.reduce((n, s) => n + s.count, 0) <= MAX_PACK_SIZE,
    {
      // The ceiling is a transaction-size limit, not taste — see MAX_PACK_SIZE.
      // Checked on the SUM because twenty slots of count three is sixty items
      // while every individual bound passes.
      message: `A pack may hold at most ${String(MAX_PACK_SIZE)} collectibles in total.`,
      path: ['slots'],
    }
  );

export const TriggerFields = z.object({
  name: z.string().min(1).max(120),
  provider: z.literal('twitch'),
  matches: z
    .array(z.object({
      type: z.enum(TWITCH_TRIGGER_TYPES),
      conditions: TriggerConditions.default({}),
    }))
    .min(1)
    .max(10),
  payouts: z.object({
    currency: z.array(z.object({
      code: z.string().min(1).max(20),
      amount: z.number().int().min(1),
    })).max(3).default([]),
    reward_ids: z.array(z.string()).max(20).default([]),
    bundle_ids: z.array(z.string()).max(5).default([]),
  }),
  enabled: z.boolean().default(true),
});

export const TriggerInput = TriggerFields
  .refine(
    (t) =>
      t.payouts.currency.length > 0 ||
      t.payouts.reward_ids.length > 0 ||
      t.payouts.bundle_ids.length > 0,
    {
      // A trigger that pays nothing fires happily and does nothing, which is
      // indistinguishable from a trigger that is broken.
      message: 'A trigger must pay out something.',
      path: ['payouts'],
    }
  );

export type BundleInput = z.infer<typeof BundleInput>;
export type TriggerInput = z.infer<typeof TriggerInput>;

/**
 * Fields the server owns. Sending any of them is a 400, not a silent drop.
 *
 * L17 — `types` is derived from `matches`. Their rule was "every writer must
 * set it", which one writer eventually forgets, and a trigger missing it is
 * invisible to the query and SILENTLY NEVER FIRES. Refusing it as input means
 * no writer is allowed to supply it, so none can forget.
 *
 * Rejecting rather than ignoring matters: a client that sends `types` believes
 * it is controlling something. Silently discarding it would leave them
 * debugging why their value had no effect.
 */
export const SERVER_OWNED_FIELDS = [
  'id', 'channel_id', 'types', 'payouts_version', 'created_at',
  // Summed from the supply counters on every read. A client that sent it
  // would be declaring a print run it has not printed — and Zod would strip
  // it silently, which is the "your value had no effect" case this list
  // exists to prevent.
  'stock',
] as const;

export function assertNoServerOwnedFields(body: unknown): string[] {
  if (typeof body !== 'object' || body === null) return [];
  return SERVER_OWNED_FIELDS.filter((f) => f in (body as Record<string, unknown>));
}
